import { useState, useEffect, useCallback } from 'react';
import { auth, db } from '@/lib/firebase';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { BrokerFormData } from '@/types/broker-registration.types';
import { TOTAL_STEPS } from '@/constants/broker-registration.constants';
import { validateStep, scrollToFirstError } from '@/utils/broker-registration-validation';
import {
  saveFormToLocalStorage,
  loadFormFromLocalStorage,
  clearFormFromLocalStorage,
} from '@/services/broker-registration.service';
import { savePendingSignInEmail } from '@/services/pending-sign-in.service';
import { saveRegistrationData } from '@/services/registration-data.service';
import { sendWelcomeEmail } from '@/lib/send-welcome-email';
import { assignRoleViaNestJS } from '@/lib/registration-roles';
import { StepIndicator } from '@/components/broker-registration/StepIndicator';
import { NavigationButtons } from '@/components/broker-registration/NavigationButtons';
import { StepOne } from '@/components/broker-registration/steps/StepOne';
import { StepTwo } from '@/components/broker-registration/steps/StepTwo';
import { StepThree } from '@/components/broker-registration/steps/StepThree';
import { StepFour } from '@/components/broker-registration/steps/StepFour';
import { StepFive } from '@/components/broker-registration/steps/StepFive';
import { LanguageSelector } from '@/components/ui/language-selector';
import { useLanguage } from '@/contexts/LanguageContext';

// Initial form data (camelCase aligned with backend)
const getInitialFormData = (): BrokerFormData => ({
  currentStep: 1,
  // Step 1: Profile
  firstName: '',
  lastName: '',
  firmName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  profilePhoto: null,
  professionalDescription: '',
  addressType: '',
  addressSearch: '',
  streetAddress: '',
  city: '',
  stateProvince: '',
  zipPostalCode: '',
  country: '',
  motherTongue: '',
  spokenLanguages: [],
  // Step 2: Expertise & Licenses
  specializations: [{ industry: '', expertises: [], experienceYears: '' }],
  licenses: [
    {
      details: '',
      number: '',
      jurisdictions: [],
      otherJurisdictions: '',
    },
  ],
  // Step 3: Model
  transactionTypes: [],
  evaluationProcess: [],
  ndaEnforcement: [],
  // Step 4: Methodology (KPI)
  postSaleRole: [],
  vettingProcess: [],
  cashDownMinPercent: '',
  fixedAmountMin: '',
  fixedAmountMax: '',
  acceptedPofTypes: [],
  competingOffers: [],
  dealSizeMin: '',
  dealSizeMax: '',
  feeStructure: [],
  // Step 5: Compliance
  termsAccepted: false,
});

export default function RegisterBroker() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<BrokerFormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);

  // Load saved draft on mount
  useEffect(() => {
    const savedData = loadFormFromLocalStorage();
    if (savedData && savedData.formData) {
      setFormData(savedData.formData);
      setCurrentStep(savedData.step);
    }
  }, []);

  // Save to localStorage on formData or step change
  useEffect(() => {
    if (currentStep > 1 || Object.values(formData).some((v) => v !== '')) {
      saveFormToLocalStorage(formData, currentStep);
    }
  }, [formData, currentStep]);

  const updateFormData = useCallback((updates: Partial<BrokerFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const handleStepValidation = useCallback(
    (step: number): boolean => {
      const validation = validateStep(step, formData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        scrollToFirstError();
        return false;
      }
      setErrors({});
      return true;
    },
    [formData]
  );

  const goToStep = useCallback(
    (targetStep: number) => {
      if (targetStep <= currentStep) {
        // Can go back without validation
        setCurrentStep(targetStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Going forward requires validation
        if (handleStepValidation(currentStep)) {
          setCurrentStep(targetStep);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    [currentStep, handleStepValidation]
  );

  const handleNext = useCallback(() => {
    if (handleStepValidation(currentStep) && currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, handleStepValidation]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    // Final validation
    if (!handleStepValidation(TOTAL_STEPS)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Save email for sign-in link (localStorage + Firebase fallback)
      try {
        await savePendingSignInEmail(formData.email);
      } catch (e) {
        console.warn('Pending sign-in email save failed (non-blocking):', e);
      }

      // 1. Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      const user = userCredential.user;

      await updateProfile(user, {
        displayName: `${formData.firstName} ${formData.lastName}`,
      });

      // 2. Save profile to Firestore
      const sourcerUid = localStorage.getItem('dealSourcerRef');
      await setDoc(doc(db, 'users', user.uid), {
        email: formData.email,
        displayName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || '',
        firmName: formData.firmName || '',
        roles: ['broker'],
        status: 'pending_review',
        registrationData: (({ password, confirmPassword, profilePhoto, ...rest }) => rest)(formData),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...(sourcerUid ? { sourcedBy: sourcerUid } : {}),
      });

      await assignRoleViaNestJS(user, 'broker');

      // 3. Clear draft
      clearFormFromLocalStorage();

      // 4. Save full registration data (best-effort)
      try {
        const { profilePhoto: _pp, password: _pw, confirmPassword: _cpw, ...safeBrokerData } = formData as any;
        await saveRegistrationData(user.uid, 'broker', safeBrokerData);
      } catch (e) {
        console.warn('saveRegistrationData failed (non-blocking):', e);
      }

      // 5. Send welcome email (fire-and-forget — must NOT block success screen)
      sendWelcomeEmail({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: 'broker',
        language: language as 'en' | 'fr',
      }).catch((emailErr) => {
        console.warn('[RegisterBroker] sendWelcomeEmail failed (non-blocking):', emailErr);
      });

      setShowSuccessScreen(true);
    } catch (error) {
      console.error('Registration error:', error);
      alert(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, handleStepValidation, language]);

  // Render current step
  const renderCurrentStep = () => {
    const stepProps = {
      formData,
      updateFormData,
      errors,
      clearError,
    };

    switch (currentStep) {
      case 1:
        return <StepOne {...stepProps} />;
      case 2:
        return <StepTwo {...stepProps} />;
      case 3:
        return <StepThree {...stepProps} />;
      case 4:
        return <StepFour {...stepProps} />;
      case 5:
        return <StepFive {...stepProps} />;
      default:
        return <StepOne {...stepProps} />;
    }
  };

  // Render success screen
  if (showSuccessScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center py-8 px-4">
        <div className="max-w-xl mx-auto bg-white border border-slate-200 p-10 rounded-xl text-center shadow-xl">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="h-10 w-10 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-[#1D314D] mt-4">
            {t('broker_registration.success.title')}
          </h2>
          <p className="text-slate-600 mt-2">{t('broker_registration.success.message')}</p>
          
          {/* Email confirmation notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex items-center justify-center mb-2">
              <svg className="h-6 w-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="font-semibold text-blue-800">
                {language === 'fr' ? 'Vérifiez votre courriel' : 'Check your email'}
              </span>
            </div>
            <p className="text-blue-700 text-sm">
              {language === 'fr' 
                ? 'Vous recevrez un courriel de confirmation. Une fois confirmé, vous aurez accès à votre tableau de bord.'
                : 'You will receive a confirmation email. Once confirmed, you will have access to your dashboard.'}
            </p>
          </div>
          <div className="mt-6 flex justify-center">
            <a
              href="/"
              className="inline-flex items-center justify-center px-6 py-3 bg-[#1D314D] text-white rounded-lg hover:bg-[#2C3E50] font-medium transition-colors"
            >
              {language === 'fr' ? 'Retour à l\'accueil' : 'Back to Home'}
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Header Card - Navy */}
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="flex-shrink-0 w-32"></div>
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              {t('broker_registration.page_title')}
            </h1>
            <div className="flex-shrink-0 w-32 flex justify-end">
              <LanguageSelector />
            </div>
          </div>
          <p className="text-white/70 text-center mt-2">
            {t('broker_registration.steps.name')} {currentStep} {t('broker_registration.steps.of')} {TOTAL_STEPS}
          </p>

          {/* Step Indicator in header */}
          <div className="mt-6">
            <StepIndicator currentStep={currentStep} onStepClick={goToStep} />
          </div>
        </div>

        {/* Content Card - Light */}
        <div className="bg-white border border-slate-200 rounded-b-xl p-6 md:p-8 shadow-lg">
          <form onSubmit={(e) => e.preventDefault()}>
            <div id="step-content">{renderCurrentStep()}</div>

            <NavigationButtons
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              onBack={handleBack}
              onNext={handleNext}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          </form>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-6">
        <p className="text-slate-500 text-sm">
          {t('broker_registration.already_member')}{' '}
          <a href="/login" className="text-[#1D314D] hover:text-[#CEAB65] font-medium">
            {t('broker_registration.login')}
          </a>
        </p>
      </div>
    </div>
  );
}
